/* eslint-disable import/prefer-default-export */
import axios from '../../axios';

// ---------------------------------------------------------------------------
// RH / Recrutamento & Seleção — Onboarding de admissão (service layer,
// handoff 0065, Fase 1).
//
// O onboarding é 1:1 com o candidato e NASCE NO BACKEND quando a Entrevista 2
// dá APROVADO (não há endpoint de criação). O pipeline de status avança UM
// PASSO POR VEZ (o backend recusa pulos com 422 invalid_onboarding_transition);
// ADMITIDO só via /admit e DESQUALIFICADO só via /disqualify.
//
// Diferente dos payloads do F0 (opcionais vazios OMITIDOS), o pré-cadastro tem
// campos NULLABLE: string vazia vira `null` aqui na boundary — enviar null é o
// jeito de LIMPAR um campo do rascunho. Permissões: hr:candidate_onboarding:*.
// ---------------------------------------------------------------------------

// Onboarding do candidato — 404 significa "ainda não existe" (estado de
// domínio, não erro): retorna null. Demais erros propagam.
export async function getCandidateOnboarding(candidateId) {
   try {
      const response = await axios.get(
         `/hr/recruitment/candidates/${candidateId}/onboarding`,
      );
      return response?.data?.data?.onboarding ?? null;
   } catch (err) {
      if (err?.response?.status === 404) return null;
      throw err;
   }
}

export async function getOnboarding(onboardingId) {
   const response = await axios.get(
      `/hr/recruitment/onboardings/${onboardingId}`,
   );
   return response?.data?.data?.onboarding ?? null;
}

// Avança o pipeline — `status` DEVE ser o próximo passo (senão 422).
export async function updateOnboardingStatus(onboardingId, status) {
   const response = await axios.patch(
      `/hr/recruitment/onboardings/${onboardingId}/status`,
      { status },
   );
   return {
      onboarding: response?.data?.data?.onboarding ?? null,
      message: response?.data?.message ?? 'Etapa avançada.',
   };
}

// Passos manuais rastreados (sub-análises do background check / exame / Onvio
// / contabilidade). Envia SÓ as chaves presentes no patch (`'key' in patch`),
// permitindo null. ATENÇÃO (0067): sub-análise REPROVADO faz o backend
// desqualificar na MESMA transação — a resposta volta com o onboarding já
// DESQUALIFICADO (não é erro).
export async function updateManualSteps(onboardingId, patch = {}) {
   const body = {};
   [
      'litigation_screening_status',
      'litigation_screening_notes',
      'professional_history_status',
      'professional_history_notes',
      'admission_exam_status',
      'onvio_draft_done',
      'sent_to_accounting_at',
   ].forEach((key) => {
      if (key in patch) body[key] = patch[key];
   });

   const response = await axios.patch(
      `/hr/recruitment/onboardings/${onboardingId}/manual-steps`,
      body,
   );
   return {
      onboarding: response?.data?.data?.onboarding ?? null,
      message: response?.data?.message ?? 'Passos manuais atualizados.',
   };
}

// Rascunho de admissão (dados do futuro funcionário). Todos os campos são
// opcionais/nullable — '' vira null (limpa o campo no backend). O PATCH
// REGENERA o checklist (cargo → CNH; estado civil CASADO → certidão).
export async function updatePreCadastro(onboardingId, payload = {}) {
   const body = {};
   Object.keys(payload).forEach((key) => {
      const value = payload[key];
      body[key] = value === '' ? null : value;
   });

   const response = await axios.patch(
      `/hr/recruitment/onboardings/${onboardingId}/pre-cadastro`,
      body,
   );
   return {
      onboarding: response?.data?.data?.onboarding ?? null,
      message: response?.data?.message ?? 'Pré-cadastro salvo.',
   };
}

// Laudo do exame admissional (handoff 0068) — evidência anexada AO ONBOARDING
// (não vai pra ficha do funcionário: o exame tem validade legal). Multipart
// campo `file` (PDF/JPEG/PNG/WEBP, ≤10MB); troca o anterior se já houver.
// Aceito em qualquer status não-terminal (422 onboarding_already_finalized).
export async function uploadExamDocument(onboardingId, file) {
   const formData = new FormData();
   formData.append('file', file);

   const response = await axios.post(
      `/hr/recruitment/onboardings/${onboardingId}/exam-document`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
   );
   return {
      onboarding: response?.data?.data?.onboarding ?? null,
      message: response?.data?.message ?? 'Laudo do exame anexado.',
   };
}

// Download do laudo: { url, filename } (URL S3 presigned, ~15min). A página
// abre em nova aba (window.open). 404 exam_document_not_found se não houver.
export async function getExamDocumentDownload(onboardingId) {
   const response = await axios.get(
      `/hr/recruitment/onboardings/${onboardingId}/exam-document`,
   );
   const data = response?.data?.data ?? {};
   return {
      url: data.url ?? null,
      filename: data.filename ?? null,
   };
}

// Desqualifica (a qualquer momento antes de ADMITIDO). Candidato →
// ENCERRADO/DESQUALIFICADO no backend.
export async function disqualifyOnboarding(onboardingId, reason) {
   const body = {};
   if (reason) body.reason = reason;

   const response = await axios.post(
      `/hr/recruitment/onboardings/${onboardingId}/disqualify`,
      body,
   );
   return {
      onboarding: response?.data?.data?.onboarding ?? null,
      message: response?.data?.message ?? 'Candidato desqualificado.',
   };
}

// ADMITIR — conversão candidato → Employee. Exige status AGUARDANDO_INICIO +
// start_date + rascunho completo + candidate.cpf (422 missing_fields /
// missing_start_date / onboarding_not_ready_for_admission; 409 CPF ativo
// duplicado). Copia os documentos FILE coletados pra ficha do funcionário.
export async function admitOnboarding(onboardingId) {
   const response = await axios.post(
      `/hr/recruitment/onboardings/${onboardingId}/admit`,
   );
   const data = response?.data?.data ?? {};
   return {
      employeeId: data.employee_id ?? null,
      documentsLinked: data.documents_linked ?? 0,
      onboarding: data.onboarding ?? null,
      message: response?.data?.message ?? 'Candidato admitido.',
   };
}
